import {
  WelcomeWizard,
  type WelcomeWizardGroup,
} from "@/components/onboarding/components/welcome-wizard";
import { InterstitialOverlay } from "@/components/overlay/interstitial-overlay";
import { useAppStore } from "@/store";
import { ImportStatus } from "@/store/one-click-import/types";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

interface WelcomeOverlayProps {}

export const WelcomeOverlay = observer<WelcomeOverlayProps>(() => {
  const { store } = useAppStore();
  const onboardingInfo = store.platformInfo.onboardingInfo;
  const baseIsOpen = onboardingInfo.shouldShowWelcomeOverlay;
  const baseInitialStep = onboardingInfo.welcomeInitialStep;
  const stepGroups = onboardingInfo.welcomeStepGroups;

  // Local state for reopening wizard after import
  const [localIsOpen, setLocalIsOpen] = useState(false);
  const [localInitialStep, setLocalInitialStep] = useState<string | undefined>(undefined);

  const {
    importStatus,
    wizardStepToReopenOn,
    wizardStepToFallbackTo,
    shouldReopenWizardAfterImport,
    clearWizardReopenFlags,
    resetImportStatus,
  } = store.oneClickImport;

  // Watch import status and react accordingly
  useEffect(() => {
    // When import starts: hide wizard
    if (importStatus === ImportStatus.InProgress) {
      setLocalIsOpen(false);
      setLocalInitialStep(undefined);
      return;
    }

    const targetStep =
      importStatus === ImportStatus.Success ? wizardStepToReopenOn : wizardStepToFallbackTo;

    // When import succeeds or fails: reopen wizard on appropriate step
    if (
      (importStatus === ImportStatus.Success || importStatus === ImportStatus.Failed) &&
      shouldReopenWizardAfterImport &&
      targetStep
    ) {
      // Reopen wizard on target step (success step or fallback step)
      setLocalIsOpen(true);
      setLocalInitialStep(targetStep);

      // Clear the reopen flags and reset status
      clearWizardReopenFlags();
      resetImportStatus();
    }
  }, [
    importStatus,
    wizardStepToReopenOn,
    wizardStepToFallbackTo,
    shouldReopenWizardAfterImport,
    clearWizardReopenFlags,
    resetImportStatus,
  ]);

  // Determine final isOpen and initialStep values
  const isOpen = localIsOpen || baseIsOpen;
  const initialStep = localInitialStep || baseInitialStep;

  useTrackView(isOpen);

  const handleDismiss = (group: WelcomeWizardGroup, ...rest: string[]) => {
    // Clear local reopen state
    setLocalIsOpen(false);
    setLocalInitialStep(undefined);

    switch (group) {
      case "icp-rfi":
        store.platformInfo.onboardingInfo.markStepCompleted("user-profile");
        store.platformInfo.dismissIcpRfi({ completedSteps: rest });
        break;
      case "import-cta":
      case "post-import":
        store.platformInfo.dismissImportCta();
        break;
      case "system-permissions-meetings":
        store.platformInfo.perDeviceOnboardingInfo.dismissMeetingOnboardingSystemPermissions();
        break;
      case "system-permissions-voice":
        store.platformInfo.perDeviceOnboardingInfo.dismissVoiceOnboardingSystemPermissions();
        break;
      case "native-app-upsell":
        // Just close the modal, no special handling needed for native app upsell
        break;
    }
  };

  return (
    <InterstitialOverlay isOpen={isOpen}>
      <WelcomeWizard
        isOpen={isOpen}
        initialStep={initialStep}
        stepGroups={stepGroups}
        onClose={handleDismiss}
        onSkip={handleDismiss}
      />
    </InterstitialOverlay>
  );
});

const useTrackView = (isOpen: boolean) => {
  const { store } = useAppStore();

  useEffect(() => {
    if (!isOpen) return;
    store.metrics.trackWelcomeOverlayView();
  }, [isOpen, store.metrics]);
};
